import { CaretRightOutlined, InboxOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
  Upload,
} from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";

import PageHeader from "../Layout/PageHeader";
import VoucherList from "./VoucherList";

import { PERMISSIONS } from "../../constants/roles";
import { SERVICE_PROVIDER_DETAILS } from "../../constants/serviceProviders";
import { USER_BADGES, USER_STATUS } from "../../constants/user";
import { usePermissions } from "../App/PermissionContext";
import { VOUCHER_SETTING_LIST, NEW_USER, PROMO_PACKAGE, PROMO_PACKAGE_500, PROMO_PACKAGE_1000, PROMO_PACKAGE_3000, PROMO_PACKAGE_5000 } from "../../constants/voucher";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const Voucher = () => {
  const { permissions } = usePermissions();

  const [form] = Form.useForm();
  const [newUserSettingForm] = Form.useForm();
  const [promoPackageSettingForm] = Form.useForm();
  const [promoPackage500SettingForm] = Form.useForm();
  const [promoPackage5000SettingForm] = Form.useForm();
  const [promoPackage3000SettingForm] = Form.useForm();
  const [promoPackage1000SettingForm] = Form.useForm();
  const [dispatchForm] = Form.useForm();
  const [loading, setLoading] = useState({ create: false, index: false });

  const [vouchers, setVouchers] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });

  const [voucherType, setVoucherType] = useState("amount");
  const [redeemLimitBy, setRedeemLimitBy] = useState("count");

  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState();

  const userBadgeStatus = [
    ...Object.entries(USER_BADGES).map(([key, value]) => ({
      label: `[Badge] ${key}`,
      value,
    })),
    ...Object.entries(USER_STATUS).map(([key, value]) => ({
      label: `[Status] ${key}`,
      value,
    })),
  ];

  const config = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const fetchVouchers = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, index: true }));

    const params = {
      limit: pagination.limit,
      page: pagination.currentPage,
    };
    const res = await axios.get("/api/vouchers", { params });

    setVouchers(res.data.data || []);
    setPagination(res.data.pagination);
    setLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [pagination.currentPage, pagination.limit]);

  const formMap = {
    [NEW_USER]: newUserSettingForm,
    [PROMO_PACKAGE]: promoPackageSettingForm,
    [PROMO_PACKAGE_500]: promoPackage500SettingForm,
    [PROMO_PACKAGE_1000]: promoPackage1000SettingForm,
    [PROMO_PACKAGE_3000]: promoPackage3000SettingForm,
    [PROMO_PACKAGE_5000]: promoPackage5000SettingForm,
  };

  const fetchVoucherSetting = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));

    for (const key of Object.keys(VOUCHER_SETTING_LIST)) {
      try {
        const res = await axios.get(`/api/vouchers/setting/${key}`);
        if (formMap[key]) {
          formMap[key].setFieldsValue(res.data.data.value || {
            voucher: [],
            isAssignable: false,
          });
        }
      } catch (err) {
        if (formMap[key]) {
          formMap[key].setFieldsValue({
            voucher: [],
            isAssignable: false,
          });
        }
      }
    }

    setLoading((prev) => ({ ...prev, index: false }));
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [pagination.currentPage, pagination.limit, fetchVouchers]);

  useEffect(() => {
    form.setFieldsValue({
      type: voucherType,
      redeemLimitBy: redeemLimitBy,
    });
  }, [voucherType, redeemLimitBy]);

  useEffect(() => {
    if (fileList && fileList.length > 0) {
      setSelectedTarget(undefined);
    }
  }, [fileList]);

  useEffect(() => {
    if (selectedTarget) {
      setFileList([]);
    }
  }, [selectedTarget]);

  useEffect(() => {
    permissions?.includes(PERMISSIONS.UPDATE_VOUCHER_SETTINGS) && fetchVoucherSetting();
  }, []);

  const createHandler = async (newVoucher) => {
    try {
      const payload = {
        ...newVoucher,
        reward: {
          en: newVoucher.reward_en || "",
          zh: newVoucher.reward_zh || "",
          ms: newVoucher.reward_ms || ""
        },
        tnc: {
          en: newVoucher.tnc_en || "",
          zh: newVoucher.tnc_zh || "",
          ms: newVoucher.tnc_ms || ""
        }
      };

      delete payload.reward_en;
      delete payload.reward_zh;
      delete payload.reward_ms;
      delete payload.tnc_en;
      delete payload.tnc_zh;
      delete payload.tnc_ms; 

      await axios.post("/api/vouchers", payload);

      form.resetFields();
      fetchVouchers();
      message.success("Voucher created successfully.");
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const newUserSettingHandler = async (newSetting) => {
    try {
      await axios
        .post("/api/vouchers/setting/new-user", {
          ...newSetting,
        })
        .then(() => {
          message.success(`Voucher settings updated.`);
          fetchVoucherSetting();
        });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const promoPackageSettingHandler = (packageName) => async (newSetting) => {
    try {
      await axios.post(`/api/vouchers/setting/${packageName}`, newSetting);
      message.success(`Voucher settings updated.`);
      fetchVoucherSetting();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const toggleHandler = async (voucherId, isActive) => {
    try {
      await axios
        .patch(`/api/vouchers/${voucherId}`, {
          isActive,
        })
        .then(() => {
          message.success(`Voucher ${isActive ? "enabled" : "disabled"}.`);
          fetchVouchers();
        });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const updateHandler = async (voucherId, voucherData) => {
    try {
      await axios.patch(`/api/vouchers/${voucherId}`, voucherData).then(() => {
        message.success(`Voucher updated.`);
        fetchVouchers();
      });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await axios({
        url: "/api/vouchers/template/dispatch",
        method: "GET",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_bulk-dispatch-voucher.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const dispatchHandler = () => {
    try {
      const messageContent = (
        <Space direction="vertical">
          <Text>
            Are you sure you want to dispatch{" "}
            <strong>{selectedVouchers.length}</strong> voucher(s) to the
            recipients?
          </Text>
          <ul>
            {selectedVouchers.map((voucher) => (
              <li>{voucher.label}</li>
            ))}
          </ul>
        </Space>
      );

      const payload = new FormData();

      selectedVouchers.forEach((voucher) => {
        payload.append(`vouchers[]`, voucher.value);
      });

      if (fileList.length > 0) {
        payload.append("file", fileList[0]);
      }

      if (selectedTarget) {
        if (selectedTarget.label.includes("[Badge]"))
          payload.append("targetBadge", selectedTarget.value);

        if (selectedTarget.label.includes("[Status]"))
          payload.append("targetStatus", selectedTarget.value);
      }

      Modal.confirm({
        content: messageContent,
        title: `Dispatch Vouchers? `,
        async onOk() {
          await axios
            .post("/api/vouchers/dispatch", payload, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((res) => {
              message.success(res.data.data.message);
            })
            .catch((error) =>
              message.error(error.response.data.message || error.message)
            );
        },
      });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Voucher"
        childRoutes={[{ path: "/voucher", breadcrumbName: "Voucher" }]}
      />
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <div className="create-voucher-container">
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            {permissions?.includes(PERMISSIONS.CREATE_VOUCHER) && (
              <Collapse.Panel header="Create New Voucher">
                <Form
                  form={form}
                  name="new-voucher-form"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  autoComplete="off"
                  initialValues={{
                    category: "general",
                    uses: 1,
                    tags: []
                  }}
                  onFinish={createHandler}
                >
                  <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      options={[
                        { label: "General", value: "general" },
                        { label: "Order", value: "order" },
                      ]}
                      defaultValue={"general"}
                      optionType="button"
                      buttonStyle="solid"
                      onChange={(e) => {
                        const value = e.target.value;
                        form.resetFields();
                        form.setFieldsValue({
                          category: value,
                          type: voucherType,
                          redeemLimitBy: redeemLimitBy,
                          uses: 1,
                          tags: []
                        });
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Voucher Name"
                    name="name"
                    rules={[
                      { required: true },
                      { max: 50, message: "Max 50 characters" },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item label="Voucher Description" name="description">
                    <TextArea />
                  </Form.Item>

                  <Form.Item
                    label="Tags"
                    name="tags"
                    extra="Add tags to categorize vouchers (e.g., 'new user', 'spx')"
                  >
                    <Select mode="tags" placeholder="Enter tags" />
                  </Form.Item>

                  {/* <Form.Item label="Image URL" name="imageUrl">
                    <Input placeholder="https://example.com/voucher-image.png" />
                  </Form.Item>  */}

                  <Divider>Multilingual Reward Description</Divider>

                  <Form.Item
                    label="Reward (English)"
                    name="reward_en"
                  >
                    <TextArea rows={2} placeholder="Enter reward description in English" />
                  </Form.Item>

                  <Form.Item
                    label="Reward (Chinese)"
                    name="reward_zh"
                  >
                    <TextArea rows={2} placeholder="Enter reward description in Chinese" />
                  </Form.Item>

                  <Form.Item
                    label="Reward (Malay)"
                    name="reward_ms"
                  >
                    <TextArea rows={2} placeholder="Enter reward description in Malay" />
                  </Form.Item>

                  <Divider>Multilingual Terms & Conditions</Divider>

                  <Form.Item
                    label="T&C (English)"
                    name="tnc_en"
                  >
                    <TextArea rows={3} placeholder="Enter terms & conditions in English" />
                  </Form.Item>

                  <Form.Item
                    label="T&C (Chinese)"
                    name="tnc_zh"
                  >
                    <TextArea rows={3} placeholder="Enter terms & conditions in Chinese" />
                  </Form.Item>

                  <Form.Item
                    label="T&C (Malay)"
                    name="tnc_ms"
                  >
                    <TextArea rows={3} placeholder="Enter terms & conditions in Malay" />
                  </Form.Item>

                  <Divider>Voucher Details</Divider>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) =>
                      prev.category !== curr.category
                    }
                  >
                    {({ getFieldValue }) => {
                      const category = getFieldValue("category");
                      return (
                        <Form.Item
                          label="Voucher Type"
                          name="type"
                          rules={[{ required: true }]}
                        >
                          <Select
                            value={voucherType}
                            onChange={setVoucherType}
                            defaultValue="amount"
                          >
                            <Select.Option value="amount">
                              Amount (RM)
                            </Select.Option>
                            <Select.Option
                              value="percent"
                              disabled={category === "order"}
                            >
                              Percentage (%)
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) =>
                      prev.category !== curr.category
                    }
                  >
                    {({ getFieldValue }) => {
                      const category = getFieldValue("category");

                      if (category === "order") return null;

                      return (
                        <Form.Item
                          extra={`Minimum total checkout value to apply this voucher.`}
                          label="Minimum Order Value"
                          name="minSpend"
                          rules={[
                            { required: true },
                            { type: "number", min: 1.0 },
                          ]}
                        >
                          <InputNumber precision={2} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  {voucherType === "amount" && (
                    <Form.Item
                      extra={`Amount that can be discounted from Order total.`}
                      label="Discount Amount (RM)"
                      name="amount"
                      rules={[{ required: true }, { type: "number", min: 0.1 }]}
                    >
                      <InputNumber precision={2} />
                    </Form.Item>
                  )}
                  {voucherType === "percent" && (
                    <Form.Item
                      extra={`Percentage that can be discounted from Order total.`}
                      label="Discount Percentage (%)"
                      name="percent"
                      rules={[
                        { required: true },
                        { type: "number", min: 1, max: 100 },
                      ]}
                    >
                      <InputNumber />
                    </Form.Item>
                  )}

                  <Form.Item
                    extra={`Period where this discount code is valid.`}
                    label="Validity Days"
                    name="validDays"
                    rules={[{ required: true }]}
                  >
                    <InputNumber />
                  </Form.Item>

                  <Form.Item
                    extra="Method of limiting voucher redemptions."
                    label="Redemption Limit Method"
                    name="redeemLimitBy"
                    rules={[{ required: true }]}
                  >
                    <Select
                      value={redeemLimitBy}
                      onChange={setRedeemLimitBy}
                      defaultValue="count"
                    >
                      <Option value="count">Count</Option>
                      <Option value="total">Total Value (RM)</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    extra={`Maximum ${redeemLimitBy === "count" ? "count" : "total value"
                      } of vouchers that are redeemable. Voucher will be automatically disabled once this limit is reached.`}
                    label="Redemption Limit"
                    name="redeemLimit"
                    rules={[{ required: true }]}
                  >
                    <InputNumber />
                  </Form.Item>

                  <Form.Item
                    extra={`Number of times the voucher can be redeemed.`}
                    label="Uses"
                    name="uses"
                    rules={[{ required: true }]}
                  >
                    <InputNumber defaultValue={1} />
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) =>
                      prev.category !== curr.category
                    }
                  >
                    {({ getFieldValue }) => {
                      const category = getFieldValue("category");

                      if (category !== "order") return null;

                      return (
                        <Form.Item
                          extra={`Orders for these couriers are elligible to apply this voucher. Leave empty for all couriers.`}
                          label="Couriers"
                          name="serviceProviders"
                        >
                          <Select
                            options={SERVICE_PROVIDER_DETAILS.map(
                              (serviceProvider) => ({
                                label: serviceProvider.name,
                                value: serviceProvider.key,
                              })
                            )}
                            mode="multiple"
                            allowClear
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  <Form.Item
                    extra={`(Optional) Date where voucher will be invalidated.`}
                    label="Expiry Date"
                    name="expiryDate"
                  >
                    <DatePicker />
                  </Form.Item>

                  <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Button type="primary" htmlType="submit">
                      Create
                    </Button>
                  </Form.Item>
                </Form>
              </Collapse.Panel>
            )}
            {permissions?.includes(PERMISSIONS.UPDATE_VOUCHER_SETTINGS) && (
              <Collapse>
                {Object.values(VOUCHER_SETTING_LIST).map((item) => {
                  const handlerMap = {
                    [NEW_USER]: newUserSettingHandler,
                    [PROMO_PACKAGE]: promoPackageSettingHandler(PROMO_PACKAGE),
                    [PROMO_PACKAGE_500]: promoPackageSettingHandler(PROMO_PACKAGE_500),
                    [PROMO_PACKAGE_1000]: promoPackageSettingHandler(PROMO_PACKAGE_1000),
                    [PROMO_PACKAGE_3000]: promoPackageSettingHandler(PROMO_PACKAGE_3000),
                    [PROMO_PACKAGE_5000]: promoPackageSettingHandler(PROMO_PACKAGE_5000),
                  };

                  const form = formMap[item.key];
                  const onFinish = handlerMap[item.key];

                  if (!form || !onFinish) return null;

                  return (
                    <Collapse.Panel key={item.key} header={item.label}>
                      <Form
                        form={form}
                        name={`${item.key}-voucher-setting-form`}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        autoComplete="off"
                        onFinish={onFinish}
                        layout="horizontal"
                      >
                        <Form.Item label="Vouchers" name="voucher" required>
                          <Select
                            options={vouchers.map((voucher) => ({
                              label: voucher.name,
                              value: voucher.objectId,
                            }))}
                            mode="multiple"
                            allowClear
                          />
                        </Form.Item>

                        <Form.Item
                          label="Enabled"
                          name="isAssignable"
                          valuePropName="checked"
                          required
                        >
                          <Switch />
                        </Form.Item>

                        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                          <Button type="primary" htmlType="submit">
                            Save
                          </Button>
                        </Form.Item>
                      </Form>
                    </Collapse.Panel>
                  );
                })}
              </Collapse>
            )}

            {permissions?.includes(PERMISSIONS.DISPATCH_VOUCHER) && (
              <Collapse.Panel header="Bulk Dispatch Voucher">
                <Form
                  form={dispatchForm}
                  name="bulk-dispatch-voucher-form"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  autoComplete="off"
                  onFinish={dispatchHandler}
                  layout="horizontal"
                >
                  <Form.Item label="Vouchers" required>
                    <Select
                      options={vouchers.map((voucher) => ({
                        label: voucher.name,
                        value: voucher.objectId,
                      }))}
                      mode="multiple"
                      value={selectedVouchers}
                      onChange={setSelectedVouchers}
                      labelInValue
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item label="Recipients" required>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Select
                        options={userBadgeStatus}
                        value={selectedTarget}
                        onChange={setSelectedTarget}
                        labelInValue
                      />
                      <Divider>or</Divider>
                      <Upload.Dragger {...config}>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file to this area to upload
                        </p>
                      </Upload.Dragger>
                    </Space>
                  </Form.Item>

                  <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={
                          selectedVouchers.length === 0 ||
                          (fileList.length === 0 && !selectedTarget)
                        }
                      >
                        Dispatch
                      </Button>
                      <Button type="secondary" onClick={downloadTemplate}>
                        Download Template
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Collapse.Panel>
            )}
          </Collapse>
        </div>
      </Space>

      <Row className="mt-2 mb-2" justify="end" align="middle">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>
      <VoucherList
        vouchers={vouchers}
        loading={loading.index}
        onToggle={toggleHandler}
        onUpdate={updateHandler}
      />
      <Row className="mt-2 mb-2" justify="end" align="middle">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) =>
              setPagination({ ...pagination, limit, currentPage })
            }
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Voucher;
